/**
 * Travelers Rest Events — Dynamic Proposal / Contract Generator
 * Listens for 'tr:estimate-ready' dispatched by quote-calculator.js,
 * populates all [data-proposal-field] elements, controls the modal,
 * and handles PDF download via the Worker /generate-pdf endpoint.
 *
 * Loaded via: {{ 'proposal.js' | asset_url | script_tag }}
 *
 * ┌──────────────────────────────────────────────────────────────────────────┐
 * │ PARITY MANIFEST — modal preview ↔ email PDF                              │
 * │                                                                          │
 * │ The in-browser modal (this file + the proposal-page-N.liquid snippets +  │
 * │ assets/proposal.css) is one of two renderers for the same proposal. The  │
 * │ email-attached PDF is rendered server-side by                            │
 * │ scripts/deposit-worker/pdf-templates.js. Drift between the two has       │
 * │ already shipped customer-visible bugs (brunch menu, 2026-04-27).         │
 * │                                                                          │
 * │ When you change any of these surfaces in this file, update the           │
 * │ corresponding surface in pdf-templates.js in the same commit:            │
 * │   • MENU_DATA + HORS_*, BUFFET/BRUNCH_ITEMS, STATIONS, PLATED_ENTREES    │
 * │   • Density tier thresholds (≥22 dense, ≤12 sparse) + the totalItems    │
 * │     counting logic in populateMenuPage()                                 │
 * │   • Bar tier (BAR_TIER_MAP) and BAR_CATEGORIES                           │
 * │   • CSS classes proposal-page--menu-dense / --sparse / --exhibit-a      │
 * │     (the actual CSS lives in assets/proposal.css for this file and is    │
 * │     duplicated in pdf-templates.js getCSS())                             │
 * │                                                                          │
 * │ See memory: feedback_main_course_key_propagation.md, feedback_labels_vs_ │
 * │ keys.md, feedback_proposal_renderer_parity.md.                           │
 * └──────────────────────────────────────────────────────────────────────────┘
 */
(function () {
  'use strict';

  /* ------------------------------------------------------------------
     CONSTANTS
  ------------------------------------------------------------------ */
  var TAX_RATE        = 0.08375;
  var BOOKING_FEE_PCT = 0.10;
  var BOOKING_FEE_MIN = 300;
  var VENUE_NAME      = 'Travelers Rest';
  var VENUE_ADDRESS   = '25 Route 100, Ossining, NY 10562';
  var VENUE_EMAIL     = 'events@WestchesterParty.com';
  var VENUE_PHONE     = '(914) 440-3365';
  var VENUE_REP       = 'Nelson Salazar';

  // Capitalize the first letter of each word, preserving any intentional
  // caps the customer typed (smith→Smith, o'brien→O'Brien, while McDonald
  // and DeVito stay intact). Mirrored in quote-calculator.js + worker pdf-templates.js.
  function titleCaseName(str) {
    return (str || '').trim().replace(/\b\w/g, function (c) { return c.toUpperCase(); });
  }

  var EVENT_TYPE_LABELS = {
    wedding:             'Wedding Reception',
    mitzvah:             'Bar/Bat Mitzvah Celebration',
    corporate:           'Corporate Event',
    bridal_shower:       'Bridal Shower Celebration',
    baby_shower:         'Baby Shower Celebration',
    celebration_of_life: 'Celebration of Life',
    milestone_birthday:  'Milestone Birthday Celebration'
  };

  var ROOM_LOOKUP = {
    'atlantic-room':   'The Atlantic Room',
    'crystal-room':    'The Crystal Room',
    'terrace-room':    'The Terrace Room',
    'lantern-room':    'The Lantern Room',
    'lobby-bar':       'The Lobby Bar',
    'outdoor-gardens': 'Outdoor Gardens'
  };

  /* Bar tier: package key → numeric tier */
  var BAR_TIER_MAP = {
    bar_soft_drinks: 0,
    wine_beer:       1,
    bar_cocktails:   2,
    bar_premium:     3
  };

  /* Bar category definitions for Exhibit B grid */
  var BAR_CATEGORIES = [
    { label: 'Red, White, Sparkling Wines & Mimosas', minTier: 1 },
    { label: 'Domestic & Imported Beer',              minTier: 1 },
    { label: 'Cocktails House Brands',                minTier: 2 },
    { label: 'Signature Cocktail',                    minTier: 2 },
    { label: 'Premium Cocktails',                     minTier: 3 },
    { label: 'Wine Service at Table',    enhancement: 'bar_wine_table' },
    { label: 'Sparkling Wine Toast',     enhancement: 'bar_sparkling_toast' },
    { label: 'Champagne & Cocktail Wall', enhancement: 'bar_champagne_wall', rental: true },
    { label: 'Mocktail Bar',             enhancement: 'bar_add_mocktail' },
    { label: 'Aperol Spritz & Hugo Spritz', enhancement: 'bar_spritz_upgrade' },
    // Espresso Martinis were the only bar enhancement with no row, so a client
    // paying $8-12/pp saw it in Exhibit E and nowhere on the bar table.
    // includedAtTier states Premium Open Bar's free inclusion, which the
    // calculator applies but the contract never mentioned.
    { label: 'Espresso Martinis',        enhancement: 'bar_espresso_martinis', includedAtTier: 3 },
    { label: 'Espresso & Cappuccino',    special: 'espresso' }
  ];

  /* ── Static menu content for Exhibit A cover page ── */

  var HORS_6 = [
    'Bacon Wrapped Scallop', 'Chicken Empanada', 'Asparagus &amp; Cheese Crisp',
    'Tempura Shrimp', 'Sesame Chicken Skewer', 'Spring Roll'
  ];
  var HORS_4 = [
    'Chicken Empanada', 'Tempura Shrimp', 'Sesame Chicken Skewer', 'Spring Roll'
  ];
  var HORS_HEAVY = [
    'Bacon Wrapped Scallop', 'Chicken Empanada', 'Asparagus &amp; Cheese Crisp',
    'Tempura Shrimp', 'Sesame Chicken Skewer', 'Spring Roll',
    'Beef Slider', 'Lobster Roll'
  ];

  /* ── The steak entrée is COMPOSED, never written out per combination ──
     Owner-reported 2026-08-31: a filet upgrade left the menu naming NY Strip,
     and a lobster add appeared nowhere in the dish it is served with. Both stay
     listed under Entrée Enhancements too — deliberate, it acknowledges the buy.

     The shape, not the four strings, is the point:
       base   — the protein, SWAPPED by an enhancement
       body   — the description, IDENTICAL in every state
       accomp — an accompaniment, APPENDED by an enhancement

     A third protein or a second accompaniment is one more entry, never a new
     branch. `label` is a lowercased fallback so this copy behaves identically
     to the Worker's, which has to serve legacy payloads carrying only labels.
     PARITY: mirrors STEAK_ENTREE / applyEntreeEnhancements() in
     scripts/deposit-worker/pdf-templates.js. Pinned against that copy by
     scripts/deposit-worker/test/entree-enhancements.test.js. */
  var STEAK_ENTREE = {
    base: 'Sliced NY Strip Loin',
    body: ' with Sauce B\u00e9arnaise',
    swaps: [
      { key: 'entree_filet_mignon', label: 'filet mignon', base: 'Filet Mignon' }
    ],
    accompaniments: [
      { key: 'entree_lobster_tails', label: 'lobster', accomp: ' &amp; Lobster Tail' }
    ]
  };

  /* The unenhanced steak line. Every menu array below is built from this
     constant so the string the composer looks for can never drift from the
     string the menus print. */
  var STEAK_ENTREE_DEFAULT = STEAK_ENTREE.base + STEAK_ENTREE.body;

  function composeSteakEntree(keys, labels) {
    var rawKeys = (keys || []).map(function (k) { return String(k).trim(); });
    var lowered = (labels || []).map(function (l) { return String(l).toLowerCase(); });
    function selected(rule) {
      return rawKeys.indexOf(rule.key) !== -1 ||
        lowered.some(function (l) { return l.indexOf(rule.label) !== -1; });
    }
    var swap = STEAK_ENTREE.swaps.filter(selected)[0];
    var line = (swap ? swap.base : STEAK_ENTREE.base) + STEAK_ENTREE.body;
    STEAK_ENTREE.accompaniments.filter(selected).forEach(function (a) { line += a.accomp; });
    return line;
  }

  /* Rewrite the steak line inside any menu array. Menus with no steak at all
     (brunch, stations, heavy hors d'oeuvres) pass through untouched, which is
     why this is safe to apply unconditionally. */
  function applyEntreeEnhancements(entrees, keys, labels) {
    if (!Array.isArray(entrees)) return entrees;
    var composed = composeSteakEntree(keys, labels);
    if (composed === STEAK_ENTREE_DEFAULT) return entrees;
    return entrees.map(function (e) {
      return e === STEAK_ENTREE_DEFAULT ? composed : e;
    });
  }

  var PLATED_ENTREES = [
    'Salmon with Heirloom Tomatoes, Shallots &amp; Capers',
    'Chicken Breast with Fontina &amp; Prosciutto',
    STEAK_ENTREE_DEFAULT,
    'Pork Tenderloin Milanese'
  ];

  var BUFFET_ITEMS = [
    'Salmon with Heirloom Tomatoes, Shallots &amp; Capers',
    'Chicken Breast with Fontina &amp; Prosciutto',
    STEAK_ENTREE_DEFAULT,
    'Pork Tenderloin Milanese',
    'Penne with Roasted Tomato Cream',
    'Caesar Salad',
    'Mashed Potatoes',
    'Roasted Vegetable'
  ];

  var BRUNCH_ITEMS = [
    'Made to Order Omelet &amp; Egg Station',
    'French Toast Casserole',
    'Carved Ham, Bacon &amp; Sausage',
    'Roasted Breakfast Potatoes',
    'Farfalle a la Pesto with Fresh Roasted Tomatoes',
    'Chicken Breast with Prosciutto and Fontina Cheese',
    'Salmon with Heirloom Tomatoes, Shallots &amp; Capers',
    'Caesar Salad',
    'Fresh Fruit &amp; Baked Goods'
  ];

  var STATIONS = [
    { name: 'Italian Station', desc: 'Chicken parmigiana, shrimp Fran\u00e7aise, penne a la vodka, farfalle with sausage and cream.' },
    { name: 'Taco Station', desc: 'Carnitas (pork), Tinga (chicken), Chorizo (pork). Served with warm tortillas, guacamole, salsa, sour cream, Jalape\u00f1os.' },
    { name: 'Asian Station', desc: 'Sesame chicken, Yangzhou fried rice, spring rolls, lo mein.' },
    { name: 'Carving Station', desc: 'NY Strip Loin, Heritage Turkey Breast, Cuban Pork Shoulder. Served with accompanying sauces and French petit-pain.' }
  ];

  // Food Stations: the sample menu shows only as many stations as the guest
  // count qualifies for, rounding DOWN to the tier the headcount fully meets
  // (2 stations = 75 guests, 3 = 100, 4 = 130). Showing a fixed 4-station spread
  // to a 78-guest event was misleading. The 1-station tier was retired 2026-08-19
  // along with the 40-guest floor, so TWO is the minimum this can return.
  // PARITY: mirrors stationsForGuests() in scripts/deposit-worker/pdf-templates.js.
  function stationsForGuests(g) {
    if (g >= 130) return 4;
    if (g >= 100) return 3;
    return 2; // food_stations is gated below 75 guests, so two is the floor
  }

  var MENU_DATA = {
    entree_plated: {
      title: 'Plated Menu Celebration',
      courses: ['Penne with Roasted Tomato Cream', 'Caesar Salad'],
      entreeHeading: 'Guests will have four entr\u00e9es to choose from\u2026',
      entrees: PLATED_ENTREES,
      entreeNote: 'Served with potatoes and vegetables'
    },
    lunch_dinner_buffet: {
      title: 'Buffet Menu Celebration',
      entreeHeading: 'Buffet offerings\u2026',
      entrees: BUFFET_ITEMS,
      entreeNote: null
    },
    brunch_buffet: {
      title: 'Brunch Buffet Celebration',
      entreeHeading: 'Brunch Buffet',
      entrees: BRUNCH_ITEMS,
      entreeNote: null,
      includesEspresso: true
    },
    food_stations: {
      title: 'Savory Station Celebration',
      stations: STATIONS
    },
    hors_doeuvres_heavy: {
      title: 'Heavily Passed Hors d\'oeuvre Celebration',
      entreeHeading: null,
      horsItems: HORS_HEAVY,
      horsHeading: null,
      horsSub: 'Passed on trays by our staff'
    }
  };

  /* ------------------------------------------------------------------
     HELPERS
  ------------------------------------------------------------------ */
  function formatCurrency(amount) {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  }

  function formatTime(timeStr) {
    if (!timeStr) return '';
    // Slot format: "6:00 PM - 10:00 PM" — already human-readable
    if (timeStr.indexOf('AM') !== -1 || timeStr.indexOf('PM') !== -1) return timeStr;
    // Legacy HH:MM (24h) fallback
    var parts = timeStr.split(':');
    var h = parseInt(parts[0], 10);
    var m = parseInt(parts[1], 10) || 0;
    var ampm = h >= 12 ? 'PM' : 'AM';
    var h12 = h % 12 || 12;
    return h12 + ':' + (m < 10 ? '0' : '') + m + ' ' + ampm;
  }

  /** Extract start and end times from a slot string like "6:00 PM - 10:00 PM" */
  function parseSlotTimes(slotStr) {
    if (!slotStr) return { start: '', end: '' };
    var idx = slotStr.indexOf(' - ');
    if (idx !== -1) {
      return { start: slotStr.substring(0, idx).trim(), end: slotStr.substring(idx + 3).trim() };
    }
    return { start: formatTime(slotStr), end: '' };
  }

  /** 5 -> "5", 5.5 -> "5.5". ⛔ Never "5.0 Hours" on a contract. */
  function formatHours(n) {
    var v = Number(n);
    if (!isFinite(v)) return '4';
    return v % 1 === 0 ? String(v) : String(Math.round(v * 10) / 10);
  }

  function formatDate(dateStr) {
    if (!dateStr) return '';
    var d = new Date(dateStr + 'T12:00:00');
    return d.toLocaleDateString('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric'
    });
  }

  /**
   * Compute payment due dates based on event date.
   * Normal: 1st = 6 weeks before, 2nd = 3 weeks before.
   * If booking is too close to the event, split remaining time equally.
   * Returns { secondDate, thirdDate } as formatted strings.
   */
  function computePaymentDates(eventDateStr) {
    if (!eventDateStr) return { secondDate: '', thirdDate: '' };

    var eventDate = new Date(eventDateStr + 'T12:00:00');
    var today = new Date();
    today.setHours(12, 0, 0, 0);

    var msPerDay = 86400000;
    var daysUntilEvent = Math.max(0, Math.round((eventDate - today) / msPerDay));

    var sixWeeks = 42; // 6 * 7
    var threeWeeks = 21; // 3 * 7

    var secondDays, thirdDays;

    if (daysUntilEvent >= sixWeeks) {
      // Normal schedule: 6 weeks and 3 weeks before event
      secondDays = sixWeeks;
      thirdDays = threeWeeks;
    } else {
      // Not enough time — split remaining days into 3 equal segments
      // [today] ... [1/3 mark: 2nd payment] ... [2/3 mark: 3rd payment] ... [event]
      var segment = Math.floor(daysUntilEvent / 3);
      secondDays = daysUntilEvent - segment;       // 2/3 of the way to event from today
      thirdDays = daysUntilEvent - (segment * 2);  // 1/3 of the way to event from today
    }

    var secondDate = new Date(eventDate.getTime());
    secondDate.setDate(secondDate.getDate() - secondDays);

    var thirdDate = new Date(eventDate.getTime());
    thirdDate.setDate(thirdDate.getDate() - thirdDays);

    var fmt = { month: 'numeric', day: 'numeric', year: 'numeric' };
    return {
      secondDate: secondDate.toLocaleDateString('en-US', fmt),
      thirdDate: thirdDate.toLocaleDateString('en-US', fmt)
    };
  }

  function computeHeadCountDeadline(eventDateStr) {
    if (!eventDateStr) return '';
    var d = new Date(eventDateStr + 'T12:00:00');
    d.setDate(d.getDate() - 14);
    return d.toLocaleDateString('en-US', {
      month: 'numeric', day: 'numeric', year: 'numeric'
    });
  }

  function setField(name, value) {
    var els = document.querySelectorAll('[data-proposal-field="' + name + '"]');
    for (var i = 0; i < els.length; i++) els[i].textContent = value;
  }

  function setHtml(name, html) {
    var els = document.querySelectorAll('[data-proposal-field="' + name + '"]');
    for (var i = 0; i < els.length; i++) els[i].innerHTML = html;
  }

  function showSection(id) {
    var els = document.querySelectorAll('[data-proposal-section="' + id + '"]');
    for (var i = 0; i < els.length; i++) els[i].style.display = '';
  }

  function hideSection(id) {
    var els = document.querySelectorAll('[data-proposal-section="' + id + '"]');
    for (var i = 0; i < els.length; i++) els[i].style.display = 'none';
  }

  /* ------------------------------------------------------------------
     EVENT MINIMUM
     PARITY: mirrors EVENT_MINIMUM_GUESTS / billedGuests() in
     scripts/deposit-worker/pdf-templates.js and the re-billing block in
     quote-calculator.js calculateEstimate(). The proposal must print the
     counts the subtotal was computed from, not the counts as entered.
  ------------------------------------------------------------------ */
  var EVENT_MINIMUM_GUESTS = 25;

  function billedGuests(state, estimate) {
    var adults   = state.adults || 0;
    var children = state.children || 0;
    var entered  = adults + children;

    if (estimate && estimate.billedAdults != null && estimate.billedChildren != null) {
      return {
        adults: estimate.billedAdults,
        children: estimate.billedChildren,
        entered: entered,
        applied: estimate.billedAdults !== adults || estimate.billedChildren !== children
      };
    }
    if (entered < EVENT_MINIMUM_GUESTS) {
      return { adults: EVENT_MINIMUM_GUESTS, children: 0, entered: entered, applied: true };
    }
    if (adults < EVENT_MINIMUM_GUESTS) {
      return { adults: EVENT_MINIMUM_GUESTS, children: entered - EVENT_MINIMUM_GUESTS, entered: entered, applied: true };
    }
    return { adults: adults, children: children, entered: entered, applied: false };
  }

  /* Event type is a FREE-TEXT input, so the label map almost never matches and
     its keys are case-sensitive ("wedding" mapped, "Wedding" did not).
     PARITY: eventTypeLabel() in pdf-templates.js. */
  function eventTypeLabel(state) {
    var raw = state.eventType || '';
    var mapped = EVENT_TYPE_LABELS[raw] || EVENT_TYPE_LABELS[String(raw).toLowerCase().trim()];
    if (mapped) return mapped;
    return titleCaseName(String(raw).replace(/_/g, ' '));
  }

  /* ------------------------------------------------------------------
     POPULATE PROPOSAL (master orchestrator)
  ------------------------------------------------------------------ */
  function populateProposal(state, estimate, labels, schedule) {

    /* ── 1. Client info ── */
    var clientName = (titleCaseName(state.firstName) + ' ' + titleCaseName(state.lastName)).trim();
    setField('clientName', clientName || 'TBD');
    setField('clientEmail', state.email || '');
    setField('clientPhone', state.phone || '');

    /* ── 2. Event details ── */
    var eventLabel = eventTypeLabel(state);
    setField('eventType', eventLabel);

    var formattedDate = formatDate(state.date);
    setField('eventDate', formattedDate);
    setField('exhibitDate', formattedDate);

    /* The event window comes from quote-calculator.js -> computeSchedule(), the
       ONE place it is computed: ceremony pulls the start back 30 min, the hour
       extension pushes the end out 60. ⛔ Do not re-derive either here — this
       file used to parse the RAW slot and so showed a DIFFERENT start time from
       the emailed contract on every ceremony event (found 2026-09-09).
       PARITY: pdf-templates.js buildPage2 reads the same computed string out of
       the payload's `startTime`.
       ⚠️ The fallback is the pre-2026-09-09 behaviour, deliberately: if a stale
       cached quote-calculator.js dispatches no `schedule`, degrade to what the
       page did before rather than to a wrong time. */
    var sched = schedule || {};
    var slotTimes = parseSlotTimes(sched.display || state.startTime);
    setField('startTime', slotTimes.start);
    setField('endTime', slotTimes.end);

    var duration = sched.totalHours != null
      ? sched.totalHours
      : (parseInt(state.duration, 10) || 4);
    setField('duration', formatHours(duration) + ' Hours');

    /* ⛔ NOT the same number as `duration`. A ceremony lengthens the event and
       buys NO bar hour; the extension's price is room + half the bar rate per
       person per hour, so it buys exactly one. */
    var barHours = sched.barHours != null ? sched.barHours : 4;

    var totalGuests = (state.adults || 0) + (state.children || 0);
    setField('totalGuests', totalGuests);

    var roomNames = (state.rooms || []).map(function (r) {
      return ROOM_LOOKUP[r] || r.replace(/-/g, ' ');
    }).join(', ');
    setField('eventSpace', roomNames || 'TBD');

    /* ── 3. Menu items (Exhibit A) ── */
    populateMenuItems(state, labels);

    /* ── 4. Pricing (Contract Page 2) — BILLED counts, see billedGuests() ── */
    var billed = billedGuests(state, estimate);

    setField('adultCount', billed.adults);
    setField('adultRate', formatCurrency(estimate.adultRate));
    setField('adultTotal', formatCurrency(billed.adults * estimate.adultRate));

    setField('childCount', billed.children);
    setField('childRate', formatCurrency(estimate.childRate));
    setField('childTotal', formatCurrency(billed.children * estimate.childRate));

    /* Explain a re-billed schedule, or the rows look like a mistake */
    if (billed.applied) {
      setField('minimumNote', 'Reflects our ' + EVENT_MINIMUM_GUESTS + '-guest event minimum. The first '
        + EVENT_MINIMUM_GUESTS + ' guests are always billed at the adult rate.');
      showSection('minimumNote');
    } else {
      hideSection('minimumNote');
    }

    /* Room fees */
    var RATES     = window.QuoteRates || {};
    var roomRates = RATES.rooms || {};
    var roomHtml  = '';
    (state.rooms || []).forEach(function (r) {
      var fee = roomRates[r] || 0;
      if (fee > 0) {
        roomHtml += '<tr class="proposal-price-row">' +
          '<td>' + (ROOM_LOOKUP[r] || r) + ' (venue fee)</td>' +
          '<td class="proposal-price-amount">' + formatCurrency(fee) + '</td></tr>';
      }
    });
    if (roomHtml) {
      setHtml('roomFeeLines', roomHtml);
      showSection('roomFeeSection');
    } else {
      hideSection('roomFeeSection');
    }

    setField('subtotal', formatCurrency(estimate.subtotal));

    /* Admin fee */
    setField('adminFeePct', estimate.adminFeePct + '%');
    setField('adminFeeAmount', formatCurrency(estimate.adminFee));

    /* ── 5. Tax (proposal only — never in estimate calculator) ── */
    var taxableAmount = estimate.subtotal + estimate.adminFee;
    var taxAmount     = Math.round(taxableAmount * TAX_RATE * 100) / 100;
    setField('taxAmount', formatCurrency(taxAmount));

    /* ── 6. Grand total ── */
    var grandTotal = Math.round((taxableAmount + taxAmount) * 100) / 100;
    setField('grandTotal', formatCurrency(grandTotal));

    /* ── 7. Payment schedule ── */
    var bookingFee       = Math.max(Math.round(grandTotal * BOOKING_FEE_PCT * 100) / 100, BOOKING_FEE_MIN);
    var remainingBalance = Math.round((grandTotal - bookingFee) * 100) / 100;
    var secondPayment    = Math.round(remainingBalance / 2 * 100) / 100;
    var thirdPayment     = Math.round((remainingBalance - secondPayment) * 100) / 100;

    setField('bookingFee', formatCurrency(bookingFee));

    /* Payment dates — based on event date with smart fallback.
       A "flexible date" submission has no parseable date, which used to render
       every date-derived line blank (" Payment Due", "...are due on .").
       PARITY: pdf-templates.js buildPage3. */
    var headCountDeadline = computeHeadCountDeadline(state.date);
    var datesKnown = Boolean(headCountDeadline);
    var paymentDates = computePaymentDates(state.date);

    setField('secondPayment', formatCurrency(secondPayment));
    setField('thirdPayment', formatCurrency(thirdPayment));
    setField('secondPaymentLabel', datesKnown
      ? paymentDates.secondDate + ' Payment Due'
      : 'Second Payment Due (6 weeks before the Event)');
    setField('thirdPaymentLabel', datesKnown
      ? paymentDates.thirdDate + ' Balance Due in Full Before Event'
      : 'Balance Due in Full (3 weeks before the Event)');

    setField('headCountDeadline', headCountDeadline);
    setHtml('headCountDeadlineNote', datesKnown
      ? 'Menu changes &amp; final head count are due on ' + headCountDeadline
        + '. If final Head Count is not received by Venue by ' + headCountDeadline
        + ' then ' + totalGuests + ' guests will serve as the final head count.'
      : 'Menu changes &amp; final head count are due 14 days before the Event date.'
        + ' If final Head Count is not received by Venue by then, ' + totalGuests
        + ' guests will serve as the final head count.');

    /* ── 8. Min head count: 85% of the BILLED count, floored at the event
       minimum. Without the floor a 25-28 guest party was guaranteed 22-24,
       i.e. under the 25 we actually bill.

       The BASIS is conditional (owner decision 2026-09-09,
       specs/minimum-headcount-basis.md):
         childRateElevated -> TOTAL basis, label loses the word "Adult", plus a
                              sub-floor line holding the adult count. Dropping
                              the adult basis outright would unlock the mix at
                              exactly the counts where it is worth most.
         otherwise         -> ADULT basis, unchanged. That word is the only thing
                              stopping a 100A/10C party arriving as 100C/10A.
       ⛔ The flag is childRate > childBlended, computed once in
       quote-calculator.js. It is NOT childPct > 0.20 — see the comment there.
       ⛔ Sub-floor is suppressed when it equals the headline (both on the 25
       floor), or it prints "Head Count: 25 (of which 25 at the adult rate)".
       PARITY: pdf-templates.js buildPage3() — number, label AND sub-floor. ── */
    var minAdults = Math.max(EVENT_MINIMUM_GUESTS, Math.ceil(billed.adults * 0.85));
    var minTotal  = Math.max(EVENT_MINIMUM_GUESTS, Math.ceil((billed.adults + billed.children) * 0.85));
    var headCountElevated = Boolean(estimate.childRateElevated);
    setField('minHeadCount', headCountElevated ? minTotal : minAdults);
    setField('minHeadCountLabel', headCountElevated ? 'Head Count' : 'Adult Head Count');
    if (headCountElevated && minAdults < minTotal) {
      setField('minHeadCountSubfloor', '(of which ' + minAdults + ' at the adult rate)');
      showSection('minHeadCountSubfloor');
    } else {
      setField('minHeadCountSubfloor', '');
      hideSection('minHeadCountSubfloor');
    }
    setField('headCountFallback', totalGuests);

    /* ── 9. Bar choices (Exhibit B) ── */
    populateBarChoices(state.barPackage, state.barEnhancements || [], state, labels);

    /* ── 10. Bar inventory dimming (Exhibit C) ── */
    applyBarInventoryDimming(state.barPackage);

    /* ── 11. Dynamic Menu Page (Exhibit A cover) ── */
    populateMenuPage(state, labels);

    /* ── Logo ── */
    var calcEl  = document.getElementById('quote-calculator');
    var logoUrl = calcEl ? calcEl.getAttribute('data-logo-url') : '';
    if (logoUrl) {
      var logoEls = document.querySelectorAll('[data-proposal-logo]');
      for (var i = 0; i < logoEls.length; i++) logoEls[i].src = logoUrl;
    }

    /* ── Child row visibility ── */
    if (!state.children || state.children <= 0) {
      hideSection('childPriceRow');
    } else {
      showSection('childPriceRow');
    }

    /* ── Event description for Section 1 ── */
    var descParts = [];
    descParts.push('Private event room.');
    if (state.mainCourse && labels.mainCourse) {
      descParts.push(labels.mainCourse[state.mainCourse] || '');
    }
    if (state.dessertBase && labels.dessertBase && labels.dessertBase[state.dessertBase]) {
      descParts.push(labels.dessertBase[state.dessertBase] + ' dessert service.');
    }
    if (state.barPackage && labels.bar) {
      // A cash bar is NOT "unlimited ... included" — guests buy their own
      // alcohol. PARITY: pdf-templates.js buildPage2 eventDescription.
      descParts.push(state.barPackage === 'bar_cash'
        ? 'Cash Bar: soft drinks, juice & mixers included; guests purchase alcoholic beverages individually.'
        : formatHours(barHours) + ' Hours unlimited ' + (labels.bar[state.barPackage] || '') + ' included.');
    }
    setField('eventDescription', descParts.join(' '));

    /* ── Under-minimum note on pricing page ── */
    populateUnderMinimumNote(state, estimate);

    /* ── 12. Itemized Price Breakdown (Exhibit E — Page 8) ── */
    try { populateItemizedBreakdown(state, estimate); } catch (err) { console.error('Itemized breakdown error:', err); }
  }

  /* ------------------------------------------------------------------
     ITEMIZED PRICE BREAKDOWN (Exhibit E — Page 8)
  ------------------------------------------------------------------ */
  function populateItemizedBreakdown(state, estimate) {
    var items = estimate.lineItems || [];
    // `isEvent` marks an EVENT-level charge that is neither food nor bar — the
    // hour extension (2026-08-31) is the first. It gets its own group; without
    // one it would print under "Food & Catering".
    // PARITY: pdf-templates.js buildPage8.
    var foodItems = items.filter(function(i) { return !i.isBar && !i.childOnly && !i.isEvent; });
    var barItems  = items.filter(function(i) { return i.isBar; });
    var eventItems = items.filter(function(i) { return i.isEvent && !i.childOnly; });
    var childOnlyItems = items.filter(function(i) { return i.childOnly; });

    function itemRow(label, rate) {
      return '<tr class="proposal-price-row">' +
        '<td>' + label + '</td>' +
        '<td class="proposal-price-amount">' + formatCurrency(rate) + '/pp</td></tr>';
    }

    /* Food items */
    if (foodItems.length) {
      var foodHtml = '';
      foodItems.forEach(function(i) { foodHtml += itemRow(i.label, i.rate); });
      setHtml('itemizedFoodRows', foodHtml);
      showSection('itemizedFoodSection');
    } else {
      hideSection('itemizedFoodSection');
    }

    /* Bar items */
    if (barItems.length) {
      var barHtml = '';
      barItems.forEach(function(i) { barHtml += itemRow(i.label, i.rate); });
      setHtml('itemizedBarRows', barHtml);
      showSection('itemizedBarSection');
    } else {
      hideSection('itemizedBarSection');
    }

    /* Event-level items (hour extension) */
    if (eventItems.length) {
      var eventHtml = '';
      eventItems.forEach(function(i) { eventHtml += itemRow(i.label, i.rate); });
      setHtml('itemizedEventRows', eventHtml);
      showSection('itemizedEventSection');
    } else {
      hideSection('itemizedEventSection');
    }

    /* Children-only items */
    if (childOnlyItems.length && state.children > 0) {
      var childHtml = '';
      childOnlyItems.forEach(function(i) { childHtml += itemRow(i.label, i.rate); });
      setHtml('itemizedChildRows', childHtml);
      showSection('itemizedChildSection');
    } else {
      hideSection('itemizedChildSection');
    }

    /* Room fees */
    var RATES     = window.QuoteRates || {};
    var roomRates = RATES.rooms || {};
    var roomHtml  = '';
    (state.rooms || []).forEach(function(r) {
      var fee = roomRates[r] || 0;
      if (fee > 0) {
        roomHtml += '<tr class="proposal-price-row">' +
          '<td>' + (ROOM_LOOKUP[r] || r) + '</td>' +
          '<td class="proposal-price-amount">' + formatCurrency(fee) + '</td></tr>';
      }
    });
    if (roomHtml) {
      setHtml('itemizedRoomRows', roomHtml);
      showSection('itemizedRoomSection');
    } else {
      hideSection('itemizedRoomSection');
    }

    /* Rate summary */
    setField('itemizedAdultRate', formatCurrency(estimate.adultRate));
    if (state.children > 0) {
      setField('itemizedChildRate', formatCurrency(estimate.childRate));
      showSection('itemizedChildRateSummary');
    } else {
      hideSection('itemizedChildRateSummary');
    }

    /* Grand total section */
    setField('itemizedAdultCount', state.adults || 0);
    setField('itemizedAdultRateInline', formatCurrency(estimate.adultRate));
    setField('itemizedAdultTotal', formatCurrency((state.adults || 0) * estimate.adultRate));

    if (state.children > 0) {
      setField('itemizedChildCount', state.children);
      setField('itemizedChildRateInline', formatCurrency(estimate.childRate));
      setField('itemizedChildTotal', formatCurrency(state.children * estimate.childRate));
      showSection('itemizedChildTotalRow');
    } else {
      hideSection('itemizedChildTotalRow');
    }

    /* Room fee lines in total section */
    var roomFeeLinesHtml = '';
    (state.rooms || []).forEach(function(r) {
      var fee = roomRates[r] || 0;
      if (fee > 0) {
        roomFeeLinesHtml += '<tr class="proposal-price-row">' +
          '<td>' + (ROOM_LOOKUP[r] || r) + ' (venue fee)</td>' +
          '<td class="proposal-price-amount">' + formatCurrency(fee) + '</td></tr>';
      }
    });
    if (roomFeeLinesHtml) {
      setHtml('itemizedRoomFeeLines', roomFeeLinesHtml);
      showSection('itemizedRoomFeeSection');
    } else {
      hideSection('itemizedRoomFeeSection');
    }

    setField('itemizedSubtotal', formatCurrency(estimate.subtotal));
    setField('itemizedAdminPct', estimate.adminFeePct + '%');
    setField('itemizedAdminFee', formatCurrency(estimate.adminFee));

    var taxableAmount = estimate.subtotal + estimate.adminFee;
    var taxAmount     = Math.round(taxableAmount * TAX_RATE * 100) / 100;
    setField('itemizedTaxAmount', formatCurrency(taxAmount));

    var grandTotal = Math.round((taxableAmount + taxAmount) * 100) / 100;
    setField('itemizedGrandTotal', formatCurrency(grandTotal));
  }

  /* ------------------------------------------------------------------
     UNDER-MINIMUM NOTE — red warning on PDF pricing page
     Reads BOOKING_MINIMUMS from the quote-calculator.js scope via window
  ------------------------------------------------------------------ */
  function populateUnderMinimumNote(state, estimate) {
    // Access BOOKING_MINIMUMS and helpers from the quote calc scope
    // They share the same page so we can check for the shortfall
    var BOOKING_MINIMUMS = window.__TR_BOOKING_MINIMUMS;
    if (!BOOKING_MINIMUMS || !state.rooms || !state.rooms.length || !state.date || !state.startTime) {
      hideSection('underMinimumNote');
      return;
    }

    var roomKey = state.rooms[0];
    var roomMins = BOOKING_MINIMUMS[roomKey];
    if (!roomMins) { hideSection('underMinimumNote'); return; }

    // Get day name
    var d = new Date(state.date + 'T00:00:00');
    var dayName = ['sunday','monday','tuesday','wednesday','thursday','friday','saturday'][d.getDay()];
    if (!dayName || !roomMins[dayName]) { hideSection('underMinimumNote'); return; }

    // Get AM/PM from end time
    var endPart = state.startTime.split(' - ')[1];
    if (!endPart) { hideSection('underMinimumNote'); return; }
    var match = endPart.trim().match(/^(\d{1,2}):(\d{2})\s*(AM|PM)$/i);
    if (!match) { hideSection('underMinimumNote'); return; }
    var h = parseInt(match[1], 10);
    var period = match[3].toUpperCase();
    var h24 = h;
    if (period === 'PM' && h !== 12) h24 += 12;
    if (period === 'AM' && h === 12) h24 = 0;
    var timePeriod = h24 <= 18 ? 'am' : 'pm';

    var min = roomMins[dayName][timePeriod];
    if (!min) { hideSection('underMinimumNote'); return; }

    var currentTotal = estimate.subtotal;
    var shortfall = min.dollars - currentTotal;

    if (shortfall <= 0) {
      hideSection('underMinimumNote');
      return;
    }

    var guestCount = (state.adults || 0) + (state.children || 0);
    var perPerson = guestCount > 0 ? Math.ceil(shortfall / guestCount) : shortfall;
    var perPersonFmt = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(perPerson);

    var roomNames = {
      'atlantic-room': 'The Atlantic Room', 'crystal-room': 'The Crystal Room',
      'terrace-room': 'The Terrace Room', 'lantern-room': 'The Lantern Room'
    };
    var roomLabel = roomNames[roomKey] || roomKey;
    var dayLabel = dayName.charAt(0).toUpperCase() + dayName.slice(1);
    var periodLabel = timePeriod === 'am' ? ' during the day' : ' evenings';

    var noteHtml = '<p style="margin-top:0.75rem; padding:0.6rem 0.75rem; background:rgba(153,27,27,0.06); border:1.5px solid #991b1b; border-radius:4px; color:#991b1b; font-size:0.78rem; line-height:1.5; font-weight:600;">'
      + 'Note: Current selections are ' + perPersonFmt + ' per person below the minimum for ' + roomLabel + ' on ' + dayLabel + periodLabel + '. Please contact our events team to discuss adjustments.'
      + '</p>';

    setHtml('underMinimumNote', noteHtml);
    showSection('underMinimumNote');
  }

  /* ------------------------------------------------------------------
     MENU ITEMS (Exhibit A)
  ------------------------------------------------------------------ */
  function populateMenuItems(state, labels) {

    /* Hors d'oeuvres */
    if (state.horsOeuvres && labels.horsOeuvres && labels.horsOeuvres[state.horsOeuvres]) {
      setField('horsOeuvresItem', labels.horsOeuvres[state.horsOeuvres]);
      showSection('horsOeuvresSection');
    } else {
      hideSection('horsOeuvresSection');
    }

    /* Courses */
    var coursesHtml = '';
    (state.courses || []).forEach(function (key) {
      var lbl = (labels.courses && labels.courses[key]) || key.replace(/_/g, ' ');
      coursesHtml += '<li>' + lbl + '</li>';
    });
    if (coursesHtml) {
      setHtml('coursesList', coursesHtml);
      showSection('coursesSection');
    } else {
      hideSection('coursesSection');
    }

    /* Main course */
    if (state.mainCourse && labels.mainCourse && labels.mainCourse[state.mainCourse]) {
      setField('mainCourseItem', labels.mainCourse[state.mainCourse]);
      showSection('mainCourseSection');
    } else {
      hideSection('mainCourseSection');
    }

    /* Cocktail add-ons */
    var cocktailHtml = '';
    (state.cocktailAddons || []).forEach(function (key) {
      var lbl = (labels.cocktailAddons && labels.cocktailAddons[key]) || key.replace(/_/g, ' ');
      cocktailHtml += '<li>' + lbl + '</li>';
    });
    if (cocktailHtml) {
      setHtml('cocktailAddonsList', cocktailHtml);
      showSection('cocktailAddonsSection');
    } else {
      hideSection('cocktailAddonsSection');
    }

    /* Entree add-ons */
    var entreeHtml = '';
    (state.entreeAddons || []).forEach(function (key) {
      var lbl = (labels.entreeAddons && labels.entreeAddons[key]) || key.replace(/_/g, ' ');
      entreeHtml += '<li>' + lbl + '</li>';
    });
    if (entreeHtml) {
      setHtml('entreeAddonsList', entreeHtml);
      showSection('entreeAddonsSection');
    } else {
      hideSection('entreeAddonsSection');
    }

    /* Dessert base */
    if (state.dessertBase && labels.dessertBase && labels.dessertBase[state.dessertBase]) {
      setField('dessertItem', labels.dessertBase[state.dessertBase]);
      showSection('dessertSection');
    } else {
      hideSection('dessertSection');
    }

    /* Dessert add-ons */
    var dessertAddHtml = '';
    (state.dessertAddons || []).forEach(function (key) {
      var lbl = (labels.dessertAddons && labels.dessertAddons[key]) || key.replace(/_/g, ' ');
      dessertAddHtml += '<li>' + lbl + '</li>';
    });
    if (dessertAddHtml) {
      setHtml('dessertAddonsList', dessertAddHtml);
      showSection('dessertAddonsSection');
    } else {
      hideSection('dessertAddonsSection');
    }

    /* After dinner */
    if (state.afterDinner && labels.afterDinner && labels.afterDinner[state.afterDinner]) {
      setField('afterDinnerItem', labels.afterDinner[state.afterDinner]);
      showSection('afterDinnerSection');
    } else {
      hideSection('afterDinnerSection');
    }

    /* Bar summary line */
    if (state.barPackage && labels.bar) {
      setField('barSummary', labels.bar[state.barPackage] || '');
    }

    /* Bar enhancements */
    var barEnhHtml = '';
    (state.barEnhancements || []).forEach(function (key) {
      var lbl = (labels.barEnhancements && labels.barEnhancements[key]) || key.replace(/_/g, ' ');
      barEnhHtml += '<li>' + lbl + '</li>';
    });
    if (barEnhHtml) {
      setHtml('barEnhancementsList', barEnhHtml);
      showSection('barEnhancementsSection');
    } else {
      hideSection('barEnhancementsSection');
    }
  }

  /* ------------------------------------------------------------------
     BAR CHOICES (Exhibit B)
  ------------------------------------------------------------------ */
  function populateBarChoices(barPackage, barEnhancements, state, labels) {
    var tier = BAR_TIER_MAP[barPackage] !== undefined ? BAR_TIER_MAP[barPackage] : -1;
    // Cash bar = guests pay individually for alcohol. Differentiates from
    // Soft Drinks (no alcohol available at all) so we can show a more
    // accurate status for alcoholic categories than "Will not be offered".
    var isCashBar = barPackage === 'bar_cash';

    var rows = document.querySelectorAll('[data-bar-row]');
    for (var i = 0; i < rows.length; i++) {
      var row      = rows[i];
      var cat      = row.getAttribute('data-bar-row');
      var statusEl = row.querySelector('[data-bar-status]');
      if (!statusEl) continue;

      var catDef = null;
      for (var j = 0; j < BAR_CATEGORIES.length; j++) {
        if (BAR_CATEGORIES[j].label === cat) { catDef = BAR_CATEGORIES[j]; break; }
      }
      if (!catDef) continue;

      var status   = 'Will not be offered/served';
      var included = false;

      if (catDef.enhancement) {
        if (barEnhancements.indexOf(catDef.enhancement) !== -1) {
          status = 'Included';
          included = true;
        } else if (catDef.includedAtTier !== undefined && tier >= catDef.includedAtTier) {
          status = 'Included with Premium Open Bar';
          included = true;
        } else if (catDef.rental) {
          status = 'Rental Not Included';
        }
      } else if (catDef.special === 'espresso') {
        if (state.afterDinner === 'coffee_espresso_dinner' || state.afterDinner === 'coffee_espresso_brunch') {
          status = 'Included';
          included = true;
        } else if (state.mainCourse === 'food_stations') {
          status = 'Included in Station Option';
          included = true;
        } else {
          status = 'Not included with Coffee & Tea service';
        }
      } else if (catDef.minTier !== undefined) {
        if (tier >= catDef.minTier) {
          status = 'Included in Price Per Person';
          included = true;
        } else if (isCashBar) {
          status = 'Available for individual purchase';
        }
      }

      statusEl.textContent = status;
      row.classList.toggle('is-included', included);
      row.classList.toggle('is-not-offered', !included);
    }

    /* Selected tier label */
    var tierLabel = (labels && labels.bar && labels.bar[barPackage]) || barPackage || 'None';
    setField('selectedBarTier', tierLabel);
  }

  /* ------------------------------------------------------------------
     BAR INVENTORY DIMMING (Exhibit C)
     Sections above the customer's tier fade to a muted gray. Sections at
     or below their tier render in full color. Unknown tier = no dimming.
  ------------------------------------------------------------------ */
  function applyBarInventoryDimming(barPackage) {
    var tier = BAR_TIER_MAP[barPackage] !== undefined ? BAR_TIER_MAP[barPackage] : null;
    // Cash bar guests can purchase Wine & Beer (tier 1) and Spirits (tier 2)
    // individually, so those sections render full-color. Premium Spirits
    // (tier 3) stays dimmed — cash bar pours from house spirits, not the
    // premium shelf. Soft Drinks bar dims everything above tier 0 as before.
    var isCashBar = barPackage === 'bar_cash';
    var page = document.getElementById('proposal-page-6');
    if (!page) return;
    var sections = page.querySelectorAll('.proposal-inventory-section[data-min-tier]');
    for (var i = 0; i < sections.length; i++) {
      var minTier = parseInt(sections[i].getAttribute('data-min-tier'), 10);
      var dim;
      if (isCashBar && minTier <= 2) {
        dim = false;
      } else {
        dim = (tier !== null && tier < minTier);
      }
      sections[i].classList.toggle('is-dimmed', dim);
    }
  }

  /* ------------------------------------------------------------------
     DYNAMIC MENU PAGE (Exhibit A cover)
     Builds the menu content from MENU_DATA based on mainCourse +
     horsOeuvres + afterDinner + barPackage selections.
  ------------------------------------------------------------------ */
  function populateMenuPage(state, labels) {
    var mainKey = state.mainCourse;
    var menu = mainKey ? MENU_DATA[mainKey] : null;

    // Hide all optional sections first
    hideSection('menuHorsSection');
    hideSection('menuCoursesSection');
    hideSection('menuEntreesSection');
    hideSection('menuStationsSection');
    hideSection('menuEntreeAddonsSection');
    hideSection('menuCocktailSection');
    hideSection('menuDessertSection');
    hideSection('menuDessertAddonsSection');
    hideSection('menuEspressoSection');
    hideSection('menuBarSection');
    hideSection('menuEntreeNote');

    // Page 1 title: "The {LastName} {EventType}" or fall back to menu title
    var lastName = titleCaseName(state.lastName);
    var eventLabel = eventTypeLabel(state);
    var pageTitle = lastName ? 'The ' + lastName + ' ' + eventLabel : (menu ? menu.title : '');
    setField('menuTitle', pageTitle);

    if (!menu) {
      return;
    }

    // Hors d'oeuvres section (for plated/buffet with HD, or heavy HD as main)
    if (mainKey === 'hors_doeuvres_heavy') {
      // Heavy HD is the main course — show all items as the main list
      setField('menuHorsHeading', 'Passed Hors d\'oeuvres');
      var horsHtml = '';
      HORS_HEAVY.forEach(function(item) { horsHtml += '<div>' + item + '</div>'; });
      setHtml('menuHorsItems', horsHtml);
      // Show the "Passed on trays" subtitle
      showSection('menuHorsSection');
    } else if (state.horsOeuvres === 'hors_doeuvres_6_full') {
      setField('menuHorsHeading', '6 Hors d\'oeuvres');
      var h6 = '';
      HORS_6.forEach(function(item) { h6 += '<div>' + item + '</div>'; });
      setHtml('menuHorsItems', h6);
      showSection('menuHorsSection');
    } else if (state.horsOeuvres === 'hors_doeuvres_4_half') {
      setField('menuHorsHeading', '4 Hors d\'oeuvres');
      var h4 = '';
      HORS_4.forEach(function(item) { h4 += '<div>' + item + '</div>'; });
      setHtml('menuHorsItems', h4);
      showSection('menuHorsSection');
    }

    // Courses (plated only — pasta + salad)
    if (menu.courses && menu.courses.length) {
      var cHtml = '';
      menu.courses.forEach(function(c) {
        cHtml += '<div class="proposal-menu-course-item">' + c + '</div>';
      });
      setHtml('menuCourseItems', cHtml);
      showSection('menuCoursesSection');
    }

    // Entrees / main items
    if (menu.entrees && menu.entrees.length && mainKey !== 'hors_doeuvres_heavy') {
      if (menu.entreeHeading) {
        setField('menuEntreeHeading', menu.entreeHeading);
      } else {
        setField('menuEntreeHeading', '');
      }
      // The steak line is composed from the entrée enhancements before it is
      // printed: a filet upgrade renames it, a lobster add appends the
      // accompaniment. state.entreeAddons holds raw keys here, so the label
      // fallback is unused on this path — it exists for Worker parity.
      var eHtml = '';
      applyEntreeEnhancements(menu.entrees, state.entreeAddons || [], [])
        .forEach(function(item) { eHtml += '<div>' + item + '</div>'; });
      setHtml('menuEntreeItems', eHtml);
      if (menu.entreeNote) {
        setField('menuEntreeNote', menu.entreeNote);
        showSection('menuEntreeNote');
      }
      showSection('menuEntreesSection');
    }

    // Stations — capped to the number the guest count qualifies for (round down)
    if (menu.stations) {
      var stationGuests = (state.adults || 0) + (state.children || 0);
      var stationsToShow = menu.stations.slice(0, stationsForGuests(stationGuests));
      var sHtml = '';
      stationsToShow.forEach(function(s) {
        sHtml += '<div class="proposal-menu-station-block">' +
          '<p class="proposal-menu-station-name">' + s.name + '</p>' +
          '<p class="proposal-menu-station-desc">' + s.desc + '</p>' +
          '</div>';
      });
      setHtml('menuStationsContent', sHtml);
      showSection('menuStationsSection');
    }

    // Entree enhancements (Lobster Tails, Filet Mignon, Children's Menu).
    // Paid selections that used to appear only in Exhibit E's line items, so a
    // client who bought the filet upgrade got a menu still listing NY Strip.
    // PARITY: pdf-templates.js buildPage1 entreeAddonsSection.
    var menuEntreeAddonHtml = '';
    (state.entreeAddons || []).forEach(function (key) {
      var lbl = (labels.entreeAddons && labels.entreeAddons[key]) || key.replace(/_/g, ' ');
      menuEntreeAddonHtml += '<div>' + lbl + '</div>';
    });
    if (menuEntreeAddonHtml) {
      setHtml('menuEntreeAddonItems', menuEntreeAddonHtml);
      showSection('menuEntreeAddonsSection');
    } else {
      hideSection('menuEntreeAddonsSection');
    }

    // Cocktail add-ons on menu page (includes specific savory station name)
    var menuCocktailHtml = '';
    (state.cocktailAddons || []).forEach(function (key) {
      var lbl = (labels.cocktailAddons && labels.cocktailAddons[key]) || key.replace(/_/g, ' ');
      menuCocktailHtml += '<div>' + lbl + '</div>';
    });
    if (menuCocktailHtml) {
      setHtml('menuCocktailItems', menuCocktailHtml);
      showSection('menuCocktailSection');
    } else {
      hideSection('menuCocktailSection');
    }

    // Dessert
    if (state.dessertBase && labels.dessertBase && labels.dessertBase[state.dessertBase]) {
      setField('menuDessertItem', labels.dessertBase[state.dessertBase]);
      showSection('menuDessertSection');
    }
    // Dessert add-ons on menu page
    if (state.dessertAddons && state.dessertAddons.length) {
      var daParts = [];
      state.dessertAddons.forEach(function(key) {
        var lbl = (labels.dessertAddons && labels.dessertAddons[key]) || key.replace(/_/g, ' ');
        daParts.push(lbl);
      });
      setField('menuDessertAddons', daParts.join(', '));
      showSection('menuDessertAddonsSection');
    }

    // Espresso / after-dinner
    if (menu.includesEspresso) {
      setField('menuEspressoItem', 'Cappuccino, Espresso & Hot Chocolate Bar');
      showSection('menuEspressoSection');
    } else if (state.afterDinner === 'coffee_espresso_dinner' || state.afterDinner === 'coffee_espresso_brunch') {
      setField('menuEspressoItem', 'Espresso Bar');
      showSection('menuEspressoSection');
    } else if (state.afterDinner === 'coffee_tea') {
      setField('menuEspressoItem', 'Coffee & Tea Service');
      showSection('menuEspressoSection');
    }

    // Bar package
    if (state.barPackage && labels && labels.bar && labels.bar[state.barPackage]) {
      var barText = labels.bar[state.barPackage];
      // Brunch + Wine & Beer or higher includes complimentary mimosas & bellinis.
      // PARITY: mirrors pdf-templates.js buildPage1 bar section + the
      // BAR_INCLUDES_BUBBLES rule in quote-calculator.js. Tier >= 1 = wine_beer
      // and above (excludes soft drinks / cash bar).
      if (mainKey === 'brunch_buffet' && BAR_TIER_MAP[state.barPackage] >= 1) {
        barText += ' — Mimosas & Bellinis';
      }
      setField('menuBarItem', barText);
      showSection('menuBarSection');
    }

    // Dynamic spacing: count visible content items to pick density tier
    var page1 = document.getElementById('proposal-page-1');
    if (page1) {
      var totalItems = 0;

      // Hors d'oeuvres: heading + individual items
      var horsEl = page1.querySelector('[data-proposal-section="menuHorsSection"]');
      if (horsEl && horsEl.style.display !== 'none') {
        totalItems += 1; // heading
        totalItems += horsEl.querySelectorAll('[data-proposal-field="menuHorsItems"] > div').length;
      }

      // Courses (pasta, salad)
      var coursesEl = page1.querySelector('[data-proposal-section="menuCoursesSection"]');
      if (coursesEl && coursesEl.style.display !== 'none') {
        totalItems += coursesEl.querySelectorAll('.proposal-menu-course-item').length;
      }

      // Entrees: heading + items + optional note
      var entreesEl = page1.querySelector('[data-proposal-section="menuEntreesSection"]');
      if (entreesEl && entreesEl.style.display !== 'none') {
        totalItems += 1; // heading
        totalItems += entreesEl.querySelectorAll('[data-proposal-field="menuEntreeItems"] > div').length;
        var noteEl = page1.querySelector('[data-proposal-section="menuEntreeNote"]');
        if (noteEl && noteEl.style.display !== 'none') totalItems += 1;
      }

      // Entree enhancements: heading + items
      var entreeAddEl = page1.querySelector('[data-proposal-section="menuEntreeAddonsSection"]');
      if (entreeAddEl && entreeAddEl.style.display !== 'none') {
        totalItems += 1; // heading
        totalItems += entreeAddEl.querySelectorAll('[data-proposal-field="menuEntreeAddonItems"] > div').length;
      }

      // Stations (name + desc = 2 lines each)
      var stationsEl = page1.querySelector('[data-proposal-section="menuStationsSection"]');
      if (stationsEl && stationsEl.style.display !== 'none') {
        totalItems += stationsEl.querySelectorAll('.proposal-menu-station-block').length * 2;
      }

      // Cocktail hour enhancements: heading + items.
      // This section was NEVER counted — a pre-existing drift from
      // pdf-templates.js buildPage1, which has always counted it. The modal
      // therefore under-counted by up to 4 and picked too loose a density
      // tier, which clips on a fixed-height page. Found 2026-08-14.
      var cocktailEl = page1.querySelector('[data-proposal-section="menuCocktailSection"]');
      if (cocktailEl && cocktailEl.style.display !== 'none') {
        totalItems += 1; // heading
        totalItems += cocktailEl.querySelectorAll('[data-proposal-field="menuCocktailItems"] > div').length;
      }

      // Single-line sections: dessert, add-ons, espresso, bar
      ['menuDessertSection','menuDessertAddonsSection','menuEspressoSection','menuBarSection'].forEach(function(s) {
        var el = page1.querySelector('[data-proposal-section="' + s + '"]');
        if (el && el.style.display !== 'none') totalItems++;
      });

      // Apply density tier.
      // PARITY: thresholds + CSS classes must match scripts/deposit-worker/pdf-templates.js
      // (buildPage1 + getCSS) and assets/proposal.css. See parity manifest at top of file.
      page1.classList.remove('proposal-page--menu-dense', 'proposal-page--menu-xdense', 'proposal-page--sparse');
      if (totalItems >= 25) {
        // xdense is applied IN ADDITION to dense — it holds only the deltas.
        page1.classList.add('proposal-page--menu-dense', 'proposal-page--menu-xdense');
      } else if (totalItems >= 22) {
        page1.classList.add('proposal-page--menu-dense');
      } else if (totalItems <= 12) {
        page1.classList.add('proposal-page--sparse');
      }

      // CONTINUOUS FILL SCALE — PARITY with buildPage1() in
      // scripts/deposit-worker/pdf-templates.js and the calc() rules in
      // proposal.css. The tier classes above are cliffs and left 13-21 items
      // with no treatment at all; this scales type and leading smoothly so
      // every item count is covered. Calibrated 2026-09-01 by MEASURING
      // rendered fill (content height / available height), target ~0.85.
      // 🔴 The 1.42 cap is a SAFETY property: .proposal-page is
      // overflow:hidden, so an over-scaled menu is silently CLIPPED, which is
      // worse than a gappy one. Do not raise it without re-measuring.
      var menuScale = Math.min(1.42, Math.max(1, 1 + (20 - totalItems) * 0.030));
      if (menuScale > 1) page1.style.setProperty('--menu-scale', menuScale.toFixed(3));
      else page1.style.removeProperty('--menu-scale');
    }
  }

  /* ------------------------------------------------------------------
     MODAL CONTROL
  ------------------------------------------------------------------ */
  function fitModalPages() {
    var sheets = Array.prototype.slice.call(document.querySelectorAll('.proposal-page'));
    var menu = document.querySelector('#proposal-page-1 .proposal-menu-body');
    var dietary = document.querySelector('#proposal-page-1 .proposal-menu-dietary');
    sheets.forEach(function(sheet) {
      sheet.style.removeProperty('height');
      sheet.style.removeProperty('max-height');
      sheet.style.removeProperty('overflow');
      var footer = sheet.querySelector('.proposal-page-footer');
      if (footer) {
        if (!footer.hasAttribute('data-original-position')) {
          footer.setAttribute('data-original-position', footer.style.position);
          footer.setAttribute('data-original-margin', footer.style.marginTop);
        }
        footer.style.position = footer.getAttribute('data-original-position');
        footer.style.marginTop = footer.getAttribute('data-original-margin');
      }
      Array.prototype.forEach.call(sheet.children, function(child) { child.style.removeProperty('zoom'); });
    });
    if (menu) { menu.style.removeProperty('zoom'); menu.style.removeProperty('justify-content'); }
    if (dietary) dietary.style.marginTop = '1.5rem';
    if (window.innerWidth <= 768 || !menu || !dietary) return;

    function bottomOf(element) {
      var bottom = element.getBoundingClientRect().top;
      Array.prototype.forEach.call(element.querySelectorAll('*'), function(child) {
        var box = child.getBoundingClientRect();
        if (box.width && box.height) bottom = Math.max(bottom, box.bottom);
      });
      return bottom;
    }
    function menuGap() { return dietary.getBoundingClientRect().top - bottomOf(menu); }
    if (menuGap() < 12) {
      dietary.style.marginTop = '12px';
      menu.style.justifyContent = 'flex-start';
      for (var menuPercent = 100; menuPercent >= 72 && menuGap() < 12; menuPercent -= 2) {
        menu.style.zoom = menuPercent + '%';
      }
    }
    function measure(sheet) {
      var footer = sheet.querySelector('.proposal-page-footer');
      if (!footer) return null;
      var pageBox = sheet.getBoundingClientRect();
      var footBox = footer.getBoundingClientRect();
      var bottom = pageBox.top;
      Array.prototype.forEach.call(sheet.querySelectorAll('*'), function(element) {
        if (element === footer || footer.contains(element) ||
            element.classList.contains('proposal-client-ref')) return;
        var box = element.getBoundingClientRect();
        if (box.width && box.height) bottom = Math.max(bottom, box.bottom);
      });
      return { contentGap: footBox.top - bottom,
        footerGap: pageBox.bottom - footBox.bottom,
        pageGap: pageBox.bottom - bottom };
    }
    function fits(layout) {
      return layout && layout.contentGap >= 12 && layout.footerGap >= 0 && layout.pageGap >= 12;
    }
    sheets.forEach(function(sheet) {
      var layout = measure(sheet);
      if (!fits(layout)) {
        var content = Array.prototype.filter.call(sheet.children, function(child) {
          return !child.classList.contains('proposal-page-footer') &&
            !child.classList.contains('proposal-client-ref');
        });
        for (var percent = 98; percent >= 72; percent -= 2) {
          content.forEach(function(child) { child.style.zoom = percent + '%'; });
          layout = measure(sheet);
          if (fits(layout)) break;
        }
      }
      if (!fits(layout)) {
        // Last resort for an unusually long proposal: keep every line readable
        // and let this one on-screen sheet grow instead of clipping its footer.
        Array.prototype.forEach.call(sheet.children, function(child) { child.style.removeProperty('zoom'); });
        sheet.style.height = 'auto';
        sheet.style.maxHeight = 'none';
        sheet.style.overflow = 'visible';
        var footer = sheet.querySelector('.proposal-page-footer');
        if (footer) { footer.style.position = 'static'; footer.style.marginTop = '1rem'; }
      }
    });
  }

  var modalFitGeneration = 0;
  function openModal() {
    var overlay = document.querySelector('.proposal-modal-overlay');
    if (!overlay) return;
    var generation = ++modalFitGeneration;
    overlay.style.visibility = 'hidden';
    overlay.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    Promise.resolve(document.fonts && document.fonts.ready).then(function() {
      if (generation !== modalFitGeneration || !overlay.classList.contains('is-active')) return;
      fitModalPages();
      overlay.style.visibility = '';
    }).catch(function() { overlay.style.visibility = ''; });
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'proposal_modal_opened', {
        event_type: (lastData && lastData.state) ? lastData.state.eventType || '' : '',
        estimated_total: (lastData && lastData.estimate) ? lastData.estimate.total || 0 : 0
      });
    }
  }

  function closeModal() {
    var overlay = document.querySelector('.proposal-modal-overlay');
    if (!overlay) return;
    overlay.classList.remove('is-active');
    modalFitGeneration++;
    overlay.style.visibility = '';
    document.body.style.overflow = '';
    // Notify quote calculator that user viewed the proposal
    document.dispatchEvent(new CustomEvent('tr:proposal-viewed'));
  }

  /* ------------------------------------------------------------------
     PDF — Server-side generation via Cloudflare Browser Rendering
     No more html2canvas/jsPDF on the client. All PDF work happens
     on the server, which works reliably on every device.
  ------------------------------------------------------------------ */

  function getPdfEndpoint() {
    var depositUrl = window.QuoteRates && window.QuoteRates.depositEndpoint;
    if (!depositUrl) return null;
    return depositUrl.replace(/\/create-draft-order\/?$/, '/generate-pdf');
  }

  /* Build the server PDF payload. lastData.state carries raw keys; the server
     expects `barPackage` / `barEnhancements` as display labels plus
     `barPackageKey` / `barEnhancementKeys` for tier/inclusion logic. */
  function buildPdfPayload() {
    if (!lastData) return null;
    // Prefer the shared builder from quote-calculator.js — it reads LIVE state
    // and converts every field to a display label, matching the inquiry and
    // signed-contract PDFs exactly. The legacy block below only converts the
    // bar fields (raw keys for everything else, stale name) and is kept as a
    // fallback in case the calculator script hasn't loaded.
    if (typeof window.buildServerProposalPayload === 'function') {
      try { return window.buildServerProposalPayload(); } catch (e) { /* fall through to legacy */ }
    }
    var payload = Object.assign({}, lastData.state);
    var labels  = lastData.labels || {};
    var barLbls = labels.bar || {};
    var barEnhLbls = labels.barEnhancements || {};

    var rawPkg = payload.barPackage || '';
    var pkgKey = (rawPkg === 'wine_beer' && payload.cocktailTab) ? 'wine_beer_tab' : rawPkg;
    var rawEnh = Array.isArray(payload.barEnhancements) ? payload.barEnhancements.slice() : [];

    payload.barPackageKey      = pkgKey;
    payload.barEnhancementKeys = rawEnh;
    payload.barPackage         = barLbls[pkgKey] || barLbls[rawPkg] || rawPkg || '';
    payload.barEnhancements    = rawEnh.map(function (k) { return barEnhLbls[k] || k; });

    if (lastData.estimate) {
      payload.subtotal    = lastData.estimate.subtotal;
      payload.adminFee    = lastData.estimate.adminFee;
      payload.adminFeePct = lastData.estimate.adminFeePct;
      payload.adultRate   = lastData.estimate.adultRate;
      payload.childRate   = lastData.estimate.childRate;
      payload.lineItems   = lastData.estimate.lineItems;
    }
    return payload;
  }

  function downloadPdf(lastName) {
    if (typeof window.gtag === 'function') {
      window.gtag('event', 'proposal_pdf_downloaded', {
        event_type: (lastData && lastData.state) ? lastData.state.eventType || '' : ''
      });
    }

    var btn = document.getElementById('proposal-download');
    var origText = btn ? btn.innerHTML : '';
    if (btn) {
      btn.innerHTML = 'Generating PDF&hellip;';
      btn.disabled = true;
    }

    var pdfUrl = getPdfEndpoint();
    var payload = buildPdfPayload();
    if (!pdfUrl || !payload) {
      if (btn) { btn.innerHTML = origText; btn.disabled = false; }
      return;
    }

    // Show patience message after 5 seconds (cold start can take 15-20s)
    var patienceTimer = setTimeout(function() {
      if (btn && btn.disabled) btn.innerHTML = 'Almost ready&hellip;';
    }, 5000);

    // 45-second timeout — first request can take 15-20s (browser cold start)
    var controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    var fetchTimeout = setTimeout(function() {
      if (controller) controller.abort();
    }, 45000);

    var fetchOpts = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload)
    };
    if (controller) fetchOpts.signal = controller.signal;

    fetch(pdfUrl, fetchOpts).then(function(resp) {
      if (!resp.ok) throw new Error('Server returned ' + resp.status);
      return resp.blob();
    }).then(function(blob) {
      clearTimeout(fetchTimeout);
      clearTimeout(patienceTimer);
      var blobUrl = URL.createObjectURL(blob);
      // Try link-click download, fall back to opening in new tab on mobile
      try {
        var a = document.createElement('a');
        a.href = blobUrl;
        a.download = 'Travelers-Rest-Proposal' + (lastName ? '-' + titleCaseName(lastName) : '') + '.pdf';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } catch (_) {
        window.open(blobUrl, '_blank');
      }
      setTimeout(function() { URL.revokeObjectURL(blobUrl); }, 10000);
      if (btn) { btn.innerHTML = origText; btn.disabled = false; }
    }).catch(function(err) {
      clearTimeout(fetchTimeout);
      clearTimeout(patienceTimer);
      console.error('[TR-PDF] Server PDF download failed:', err);
      if (btn) {
        btn.innerHTML = 'PDF unavailable — try again';
        btn.disabled = false;
        setTimeout(function() { btn.innerHTML = origText; }, 4000);
      }
    });
  }

  /* ------------------------------------------------------------------
     EVENT LISTENERS
  ------------------------------------------------------------------ */
  var lastData = null;

  document.addEventListener('tr:estimate-ready', function (e) {
    lastData = e.detail;
  });

  document.addEventListener('click', function (e) {
    var target = e.target;

    /* "View Your Custom Proposal" button */
    if (target.id === 'tr-view-proposal' || (target.closest && target.closest('#tr-view-proposal'))) {
      if (lastData) {
        // Read live contact fields so proposal always reflects current input
        var liveState = Object.assign({}, lastData.state);
        var fnEl = document.querySelector('[data-input-firstname]');
        var lnEl = document.querySelector('[data-input-lastname]');
        var emEl = document.querySelector('[data-input-email]');
        var phEl = document.querySelector('[data-input-phone]');
        if (fnEl && fnEl.value) liveState.firstName = fnEl.value.trim();
        if (lnEl && lnEl.value) liveState.lastName  = lnEl.value.trim();
        if (emEl && emEl.value) liveState.email     = emEl.value.trim();
        if (phEl && phEl.value) liveState.phone     = phEl.value.trim();
        populateProposal(liveState, lastData.estimate, lastData.labels, lastData.schedule);
        openModal();
      }
      return;
    }

    /* Close button */
    if (target.id === 'proposal-close' || (target.closest && target.closest('#proposal-close'))) {
      closeModal();
      return;
    }

    /* Click on backdrop */
    if (target.classList && target.classList.contains('proposal-modal-overlay')) {
      closeModal();
      return;
    }

    /* Download PDF */
    if (target.id === 'proposal-download' || (target.closest && target.closest('#proposal-download'))) {
      var ln = lastData && lastData.state ? lastData.state.lastName : '';
      downloadPdf(ln);
      return;
    }
  });

  /* Escape key closes modal */
  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape') closeModal();
  });

  /* ------------------------------------------------------------------
     GENERATE PDF AS BASE64 — Server-side via Cloudflare Worker
     No client-side rendering. The Worker generates the PDF using
     Cloudflare Browser Rendering and returns base64.
  ------------------------------------------------------------------ */
  window.generateProposalPdfBase64 = async function () {
    if (!lastData) return null;
    var pdfUrl = getPdfEndpoint();
    if (!pdfUrl) return null;

    try {
      console.warn('[TR-PDF] Requesting server-side PDF...');
      var payload = buildPdfPayload();
      if (!payload) return null;
      payload._returnBase64 = true;
      var resp = await fetch(pdfUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      if (!resp.ok) {
        console.error('[TR-PDF] Server returned', resp.status);
        return null;
      }
      var data = await resp.json();
      if (data.pdfBase64) {
        console.warn('[TR-PDF] Server PDF received, base64 length:', data.pdfBase64.length);
        return data.pdfBase64;
      }
      return null;
    } catch (err) {
      console.error('[TR-PDF] Server PDF request failed:', err);
      return null;
    }
  };

})();
